import { BarChart3, Target, Zap, CheckCircle2, Mail, Phone, FileCheck, Presentation, HeadphonesIcon, Lightbulb } from 'lucide-react';
import { ServiceTargetSection, TargetAudience } from '@/components/prestations/ServiceTargetSection';
import { MethodologyTimeline } from '@/components/prestations/MethodologyTimeline';
import { UseCaseCard } from '@/components/prestations/UseCaseCard';
import { BuildingIcon, ResearchIcon, VineyardIcon } from '@/components/prestations/PrestationsIcons';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';

export function DataSciencePage() {
  const { t, i18n } = useTranslation('dataScience');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  const seoHead = <SEOHead title={t('seo.title', 'Data Science Agriculture')} description={t('seo.description', 'Conseil en data science pour l\'agriculture numerique')} path="/prestations/data-science" />;

  const targetAudiences: TargetAudience[] = [
    {
      icon: BuildingIcon,
      title: t('targetSection.audiences.cooperatives.title'),
      description: t('targetSection.audiences.cooperatives.description'),
      badge: t('targetSection.audiences.cooperatives.badge'),
    },
    {
      icon: ResearchIcon,
      title: t('targetSection.audiences.research.title'),
      description: t('targetSection.audiences.research.description'),
      badge: t('targetSection.audiences.research.badge'),
    },
    {
      icon: VineyardIcon,
      title: t('targetSection.audiences.vineyards.title'),
      description: t('targetSection.audiences.vineyards.description'),
      badge: t('targetSection.audiences.vineyards.badge'),
    },
  ];

  const useCases = [
    {
      client: t('useCases.cases.vineyards.client'),
      context: t('useCases.cases.vineyards.context'),
      challenge: t('useCases.cases.vineyards.challenge'),
      solution: t('useCases.cases.vineyards.solution'),
      result: t('useCases.cases.vineyards.result'),
      image: '/placeholder-vigor-map.jpg',
      tags: ['Python', 'Satellite', 'Drone', 'QGIS'],
      articleSlug: 'comment-synthetiser-un-historique-de-cartes-de-vegetation-sur-de-grands-parcellaires',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/comment-synthetiser-un-historique-de-cartes-de-vegetation-sur-de-grands-parcellaires.jpg',
      articleTitle: 'Comment synthétiser un historique de cartes de végétation sur de grands parcellaires ?',
    },
    {
      client: t('useCases.cases.cooperatives.client'),
      context: t('useCases.cases.cooperatives.context'),
      challenge: t('useCases.cases.cooperatives.challenge'),
      solution: t('useCases.cases.cooperatives.solution'),
      result: t('useCases.cases.cooperatives.result'),
      image: '/placeholder-yield-map.jpg',
      tags: ['R', 'QGIS', 'Shapefile'],
      articleSlug: 'filtrer-nettoyer-sa-carte-de-rendement',
      articleImage: 'https://bnbshpvnzjuayggbyjqc.supabase.co/storage/v1/object/public/articles-images/og/filtrer-nettoyer-sa-carte-de-rendement.png',
      articleTitle: 'Filtrer – Nettoyer sa carte de rendement',
    },
  ];

  const methodology = [
    {
      number: '1',
      title: t('methodology.steps.listening.title'),
      description: t('methodology.steps.listening.description'),
      icon: Phone,
    },
    {
      number: '2',
      title: t('methodology.steps.proposal.title'),
      description: t('methodology.steps.proposal.description'),
      icon: FileCheck,
    },
    {
      number: '3',
      title: t('methodology.steps.execution.title'),
      description: t('methodology.steps.execution.description'),
      icon: Zap,
    },
    {
      number: '4',
      title: t('methodology.steps.delivery.title'),
      description: t('methodology.steps.delivery.description'),
      icon: Presentation,
    },
    {
      number: '5',
      title: t('methodology.steps.followUp.title'),
      description: t('methodology.steps.followUp.description'),
      icon: HeadphonesIcon,
    },
  ];

  const services = [
    {
      icon: BarChart3,
      title: t('services.items.dataAnalysis.title'),
      items: t('services.items.dataAnalysis.features', { returnObjects: true }) as string[],
      duration: t('services.items.dataAnalysis.duration'),
    },
    {
      icon: Target,
      title: t('services.items.mapping.title'),
      items: t('services.items.mapping.features', { returnObjects: true }) as string[],
      duration: t('services.items.mapping.duration'),
    },
    {
      icon: Zap,
      title: t('services.items.consulting.title'),
      items: t('services.items.consulting.features', { returnObjects: true }) as string[],
      duration: t('services.items.consulting.duration'),
    },
  ];

  return (
    <div>
      {seoHead}
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="text-sm mb-6 opacity-90">
              <a href={`/${currentLocale}`} className="hover:underline">
                {t('breadcrumb.home')}
              </a>
              {' > '}
              <a href={`/${currentLocale}/prestations`} className="hover:underline">
                {t('breadcrumb.prestations')}
              </a>
              {' > '}
              <span>{t('breadcrumb.current')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div>
                <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-6">
                  {t('hero.title')}
                </h1>
                <p className="text-xl mb-8 opacity-95">
                  {t('hero.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`/${currentLocale}/contact`}
                    className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg text-center"
                  >
                    {t('hero.ctaPrimary')}
                  </a>
                  <a
                    href="#cas-usage"
                    className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center"
                  >
                    {t('hero.ctaSecondary')}
                  </a>
                </div>
              </div>

              {/* Visual - 3 Cards */}
              <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20">
                <div className="grid grid-cols-1 gap-4">
                  {/* Card 1: Cartographie */}
                  <div className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex-shrink-0">
                        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                          {/* Carte géographique stylisée */}
                          <rect x="10" y="10" width="80" height="80" rx="4" fill="none" stroke="white" strokeWidth="3" opacity="0.8"/>
                          {/* Lignes de quadrillage */}
                          <line x1="10" y1="40" x2="90" y2="40" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                          <line x1="10" y1="70" x2="90" y2="70" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                          <line x1="40" y1="10" x2="40" y2="90" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                          <line x1="70" y1="10" x2="70" y2="90" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                          {/* Zones de parcelles */}
                          <path d="M20 25 L35 20 L38 35 L25 38 Z" fill="white" opacity="0.6"/>
                          <path d="M50 55 L65 52 L70 68 L55 70 Z" fill="white" opacity="0.7"/>
                          <path d="M75 25 L85 28 L83 40 L72 38 Z" fill="white" opacity="0.5"/>
                          {/* Pin de localisation */}
                          <circle cx="55" cy="62" r="4" fill="#60A5FA" stroke="white" strokeWidth="2"/>
                          <path d="M55 58 L55 50" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{t('hero.visualCards.cartography.title')}</h3>
                        <p className="text-sm opacity-90">{t('hero.visualCards.cartography.description')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Données spatiales */}
                  <div className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex-shrink-0">
                        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                          {/* Satellite stylisé */}
                          <rect x="35" y="35" width="30" height="30" rx="3" fill="white" opacity="0.8"/>
                          {/* Panneaux solaires */}
                          <rect x="15" y="42" width="18" height="16" fill="none" stroke="white" strokeWidth="2" opacity="0.7"/>
                          <line x1="15" y1="50" x2="33" y2="50" stroke="white" strokeWidth="1" opacity="0.7"/>
                          <rect x="67" y="42" width="18" height="16" fill="none" stroke="white" strokeWidth="2" opacity="0.7"/>
                          <line x1="67" y1="50" x2="85" y2="50" stroke="white" strokeWidth="1" opacity="0.7"/>
                          {/* Antenne */}
                          <line x1="50" y1="35" x2="50" y2="25" stroke="white" strokeWidth="2"/>
                          <circle cx="50" cy="25" r="3" fill="white"/>
                          {/* Signaux de données */}
                          <path d="M50 70 L30 85" stroke="#60A5FA" strokeWidth="2" strokeDasharray="3,2"/>
                          <path d="M50 70 L50 88" stroke="#60A5FA" strokeWidth="2" strokeDasharray="3,2"/>
                          <path d="M50 70 L70 85" stroke="#60A5FA" strokeWidth="2" strokeDasharray="3,2"/>
                          {/* Points de données */}
                          <circle cx="30" cy="85" r="2.5" fill="#60A5FA"/>
                          <circle cx="50" cy="88" r="2.5" fill="#60A5FA"/>
                          <circle cx="70" cy="85" r="2.5" fill="#60A5FA"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{t('hero.visualCards.spatialData.title')}</h3>
                        <p className="text-sm opacity-90">{t('hero.visualCards.spatialData.description')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Modélisation */}
                  <div className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex-shrink-0">
                        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                          {/* Graphique avec courbes et points */}
                          <line x1="15" y1="85" x2="15" y2="15" stroke="white" strokeWidth="3" opacity="0.8"/>
                          <line x1="15" y1="85" x2="85" y2="85" stroke="white" strokeWidth="3" opacity="0.8"/>
                          {/* Courbe de modélisation */}
                          <path d="M20 75 Q30 65 40 55 T60 35 T80 25" fill="none" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round"/>
                          {/* Points de données */}
                          <circle cx="20" cy="75" r="3" fill="white"/>
                          <circle cx="30" cy="65" r="3" fill="white"/>
                          <circle cx="40" cy="55" r="3" fill="white"/>
                          <circle cx="50" cy="45" r="3" fill="white"/>
                          <circle cx="60" cy="35" r="3" fill="white"/>
                          <circle cx="70" cy="30" r="3" fill="white"/>
                          <circle cx="80" cy="25" r="3" fill="white"/>
                          {/* Zone de confiance */}
                          <path d="M20 75 Q30 60 40 50 T60 30 T80 20 L80 30 Q70 35 60 40 T40 60 T20 80 Z" fill="white" opacity="0.2"/>
                          {/* Équation symbolique */}
                          <text x="50" y="12" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="monospace">y = f(x)</text>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{t('hero.visualCards.modeling.title')}</h3>
                        <p className="text-sm opacity-90">{t('hero.visualCards.modeling.description')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <ServiceTargetSection
        title={t('targetSection.title')}
        subtitle={t('targetSection.subtitle')}
        audiences={targetAudiences}
        accentColor="blue"
      />

      {/* Cas d'Usage */}
      <section id="cas-usage" className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              {t('useCases.title')}
            </h2>
            <p className="text-xl text-aspexit-gray text-center mb-16 max-w-2xl mx-auto">
              {t('useCases.subtitle')}
            </p>

            <div className="space-y-12">
              {useCases.map((useCase, index) => (
                <UseCaseCard key={index} useCase={useCase} index={index} accentColor="blue" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Méthodologie */}
      <MethodologyTimeline
        title={t('methodology.title')}
        subtitle={t('methodology.subtitle')}
        steps={methodology}
        size="compact"
        accentColor="blue"
      />

      {/* Ce que je propose */}
      <section className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-12 text-center">
              {t('services.title')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow h-full flex flex-col"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-aspexit-black mb-4">
                    {service.title}
                  </h3>
                  <ul className="space-y-2 mb-6 flex-1">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-aspexit-gray">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-border mt-auto">
                    <p className="text-sm text-aspexit-gray">
                      <strong>{t('services.duration')} :</strong> {service.duration}
                    </p>
                    <p className="text-sm text-aspexit-gray mt-1">
                      <strong>{t('services.pricing')} :</strong> {t('services.onQuote')}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-aspexit-gray mt-8">
              <Lightbulb className="w-4 h-4 inline mr-1 text-blue-600" /> {t('services.note')}
            </p>
          </div>
        </div>
      </section>


      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {t('cta.subtitle')}
            </p>

            <div className="flex justify-center">
              <a
                href={`/${currentLocale}/contact`}
                className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {t('cta.button')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
